import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, X } from "lucide-react";
import { catalog, detailSlugFor } from "@/data/catalog";
import SEO from "@/components/SEO";

const brands = [
  {
    key: "epicutis",
    name: "EPICUTIS",
    image: "https://customer-assets-eiarnc6j.emergentagent.net/job_aesthetic-premium-1/artifacts/hyptgmji_5177348390185012469.jpg",
    description: "A quiet, science-led Italian skincare line built on ultra-purified lipid technology. Epicutis restores the skin barrier and returns a healthy, resilient glow — refined enough for the most sensitive skin."
  },
  {
    key: "holy-land",
    name: "HOLY LAND COSMETICS",
    image: "https://customer-assets-eiarnc6j.emergentagent.net/job_aesthetic-premium-1/artifacts/5pwt0z6i_5177348390185012470.jpg",
    description: "A physician-favourite professional line trusted by aesthetic clinics worldwide. Holy Land pairs clinical-grade peels and treatment formulas with meticulously calibrated home-care to extend in-clinic results."
  },
  {
    key: "skinbetter",
    name: "SKINBETTER SCIENCE",
    image: "https://customer-assets-eiarnc6j.emergentagent.net/job_aesthetic-premium-1/artifacts/bc6i4r00_5177348390185012467.jpg",
    description: "An award-winning American medical skincare brand acclaimed for its patented AlphaRet® and next-generation formulations. Delivers visible improvement in tone, texture and fine lines — elegantly and without irritation."
  }
];

export default function Treatments() {
  const [openBrand, setOpenBrand] = useState(null);
  const active = brands.find(b => b.key === openBrand);

  return (
    <div data-testid="treatments-page" className="pt-32 md:pt-40 bg-[#FAF8F5] font-sans">
      <SEO
        title="Treatments & Pricing | Natalia Medical Aesthetics in Kirkland, WA"
        description="Full service catalog — Morpheus8, HydraFacial, IPL Lumecca, Microneedling, PRX-T33, Skin Biorevitalization and more. Transparent pricing. Kirkland, WA."
        path="/treatments"
      />
      {/* Header */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="max-w-4xl">
          <div className="text-[0.72rem] font-medium font-medium tracking-normal text-[#8A847C] mb-6">
            Full Treatment Menu
          </div>
          <h1 className="text-[2.4rem] sm:text-5xl lg:text-[3.75rem] xl:text-[4.25rem] leading-[1.05] tracking-[-0.02em] text-[#1A1A1A] font-medium">
            The complete service catalog.
          </h1>
          <p className="mt-8 text-[1.05rem] text-[#5C5C5C] leading-[1.65] max-w-2xl font-normal">
            Every treatment is performed personally by Natalia. Prices and durations
            are indicative — your final plan is confirmed during consultation.
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
        <div className="space-y-24 md:space-y-32">
          {catalog.map((cat, ci) => (
            <div key={cat.title} data-testid={`category-${ci}`} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
              <div className="lg:col-span-4 lg:sticky lg:top-32 self-start">
                <div className="text-[0.72rem] font-medium font-medium tracking-normal text-[#8A847C] mb-5">
                  Category · {String(ci + 1).padStart(2, "0")}
                </div>
                <h2 className="text-3xl md:text-4xl lg:text-[2.6rem] leading-[1.1] tracking-[-0.02em] text-[#1A1A1A] font-medium">
                  {cat.title}
                </h2>
                <p className="mt-5 text-[#5C5C5C] leading-relaxed max-w-sm">
                  {cat.subtitle}
                </p>
              </div>

              <div className="lg:col-span-8">
                <ul className="border-t border-[#E5E0DA]">
                  {cat.items.map((it, idx) => {
                    const slug = detailSlugFor(it.name);
                    const Wrapper = slug ? Link : "div";
                    const wrapperProps = slug
                      ? { to: `/treatments/${slug}`, "data-testid": `catalog-item-${ci}-${idx}-linked` }
                      : { "data-testid": `catalog-item-${ci}-${idx}` };
                    return (
                      <li key={`${it.name}-${idx}`} className="border-b border-[#E5E0DA]">
                        <Wrapper
                          {...wrapperProps}
                          className={`group flex items-center justify-between gap-4 py-5 md:py-6 ${slug ? "cursor-pointer hover:pl-2 transition-[padding] duration-500" : ""}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-3 flex-wrap">
                              <span className="text-lg md:text-xl text-[#1A1A1A] font-medium tracking-[-0.01em]">
                                {it.name}
                              </span>
                              {it.duration && (
                                <span className="text-xs md:text-sm text-[#8A847C] tracking-wide">
                                  · {it.duration}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-4 md:gap-6 shrink-0">
                            <span className="text-base md:text-lg text-[#1A1A1A] font-medium tabular-nums">
                              {it.price}
                            </span>
                            {slug ? (
                              <ArrowUpRight
                                strokeWidth={1.5}
                                className="w-4 h-4 text-[#8A847C] group-hover:text-[#1A1A1A] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
                              />
                            ) : (
                              <span className="w-4 h-4 inline-block" />
                            )}
                          </div>
                        </Wrapper>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Professional Skincare */}
      <section data-testid="professional-skincare" className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 pb-16 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4 lg:sticky lg:top-32 self-start">
            <div className="text-[0.72rem] font-medium tracking-[0.24em] uppercase text-[#8A847C] mb-5">
              Skincare · In Clinic
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-[2.6rem] leading-[1.1] tracking-[-0.02em] text-[#1A1A1A] font-medium">
              Professional Skincare
            </h2>
            <p className="mt-5 text-[#5C5C5C] leading-relaxed max-w-sm">
              Curated skincare products available in clinic.
            </p>
          </div>

          <div className="lg:col-span-8">
            <ul className="border-t border-[#E5E0DA]">
              {brands.map((b) => (
                <li key={b.key} className="border-b border-[#E5E0DA]">
                  <button
                    type="button"
                    data-testid={`skincare-row-${b.key}`}
                    onClick={() => setOpenBrand(b.key)}
                    className="group w-full flex items-center justify-between gap-4 py-5 md:py-6 text-left cursor-pointer hover:pl-2 transition-[padding] duration-500"
                  >
                    <span className="text-lg md:text-xl text-[#1A1A1A] font-medium tracking-[-0.01em]">
                      {b.name}
                    </span>
                    <ArrowUpRight
                      strokeWidth={1.5}
                      className="w-4 h-4 text-[#8A847C] group-hover:text-[#1A1A1A] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
                    />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Brand modal */}
      {active && (
        <div
          data-testid="skincare-modal"
          className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 md:py-12"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-[#1A1A1A]/40 backdrop-blur-sm"
            onClick={() => setOpenBrand(null)}
          />
          <div className="relative bg-[#FAF8F5] rounded-[28px] w-full max-w-[880px] max-h-[92vh] overflow-y-auto shadow-[0_30px_80px_rgba(0,0,0,0.18)] border border-[#E5E0DA]">
            <button
              type="button"
              data-testid="skincare-modal-close"
              onClick={() => setOpenBrand(null)}
              aria-label="Close"
              className="absolute top-4 right-4 md:top-6 md:right-6 w-10 h-10 rounded-full bg-white border border-[#E5E0DA] flex items-center justify-center hover:bg-[#F3EFEA] transition-colors z-10"
            >
              <X strokeWidth={1.5} className="w-4 h-4" />
            </button>

            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="aspect-[4/5] md:aspect-auto bg-[#F3EFEA] overflow-hidden">
                <img src={active.image} alt={active.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-8 md:p-12 flex flex-col">
                <div className="text-[0.72rem] font-medium tracking-[0.24em] uppercase text-[#8A847C] mb-5">
                  Available in Clinic
                </div>
                <h3 className="text-2xl md:text-[2rem] leading-[1.15] tracking-[-0.02em] text-[#1A1A1A] font-medium">
                  {active.name}
                </h3>
                <p className="mt-6 text-[#5C5C5C] leading-relaxed text-[0.95rem]">
                  {active.description}
                </p>
                <p className="mt-6 text-sm text-[#1A1A1A]">Available in clinic.</p>
                <p className="mt-6 text-[#8A847C] italic leading-relaxed text-[0.9rem]">
                  Ask Natalia which products are best suited for your skin and treatment plan.
                </p>
                <div className="mt-auto pt-10">
                  <a
                    href="tel:+13608204854"
                    data-testid="skincare-modal-book"
                    className="inline-flex items-center justify-center bg-[#1A1A1A] text-white rounded-full px-8 py-4 text-[0.85rem] font-medium border border-[#1A1A1A] hover:bg-[#3A3A3A] hover:border-[#3A3A3A] transition-colors duration-300"
                  >
                    Book Now
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* CTA */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 pb-24 md:pb-32">
        <div className="bg-[#F3EFEA] rounded-[28px] p-12 md:p-24 text-center">
          <div className="text-[0.72rem] font-medium font-medium tracking-normal text-[#8A847C] mb-8">
            Not sure where to begin?
          </div>
          <h3 className="text-3xl md:text-5xl lg:text-[3.25rem] leading-[1.1] tracking-[-0.02em] max-w-3xl mx-auto font-medium text-[#1A1A1A]">
            Let us design a plan tailored to your skin.
          </h3>
          <p className="mt-6 text-[#5C5C5C] max-w-lg mx-auto">
            Every plan begins with a private consultation. We will guide you to
            the right protocol — no menu, no upselling.
          </p>
          <div className="mt-10">
            <Link
              to="/book"
              data-testid="treatments-cta-btn"
              className="inline-flex items-center justify-center bg-[#1A1A1A] text-white rounded-full px-8 py-4 text-[0.85rem] font-medium border border-[#1A1A1A] hover:bg-[#3A3A3A] hover:border-[#3A3A3A] transition-colors duration-300"
            >
              Book Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
